import React from "react";
import { motion } from "framer-motion";
import { SITE } from "@/constants/testIds";

/**
 * Capabilities — presented as a 4-step process rather than a list of services.
 * Visual, progressive, less text-heavy.
 */
const STEPS = [
  {
    n: "01",
    title: "Listen",
    sub: "Understand the problem.",
    caps: ["Discovery", "Brief Audit", "Strategy", "Creative Direction"],
  },
  {
    n: "02",
    title: "Frame",
    sub: "Decide what the story is.",
    caps: ["Concept", "Scripting", "Treatment", "Storyboarding"],
  },
  {
    n: "03",
    title: "Make",
    sub: "Produce it without compromise.",
    caps: ["Direction", "Production", "Editing", "Motion · Sound · Colour"],
  },
  {
    n: "04",
    title: "Land",
    sub: "Put it into the world.",
    caps: ["Distribution Cuts", "Campaign Films", "Content Series", "Live Experiences"],
  },
];

export default function Services() {
  return (
    <section data-testid={SITE.servicesSection} className="bg-bone text-ink py-20 md:py-24 lg:py-28">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-12 gap-8 mb-20 md:mb-24">
          <div className="col-span-12 md:col-span-6">
            <p className="eyebrow text-ink/60 mb-5">◇ Capabilities · The Process</p>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter font-medium leading-[0.95]">
              Four steps.<br/>One studio.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
            <p className="text-ink/70 text-base md:text-lg leading-relaxed">
              Every film, campaign and experience moves through the same four stages — listen, frame, make, land. We work with specialists at each step, assembled around the brief.
            </p>
          </div>
        </div>

        {/* Process row */}
        <div className="relative grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/15">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="relative bg-bone p-8 md:p-10 group"
            >
              <div className="flex items-center justify-between mb-10">
                <span className="font-mono text-xs text-ink/50">{s.n}</span>
                <span className="block w-8 h-px bg-ink/40 group-hover:bg-relatable transition-colors" />
              </div>
              <h3 className="font-display text-4xl md:text-5xl tracking-tight font-medium leading-[0.95] group-hover:text-relatable transition-colors">
                {s.title}.
              </h3>
              <p className="mt-4 font-display italic text-ink/85 text-lg md:text-xl leading-snug">
                {s.sub}
              </p>
              <ul className="mt-10 space-y-2">
                {s.caps.map((c) => (
                  <li key={c} className="eyebrow text-ink/60 flex items-center gap-2">
                    <span className="block w-1 h-1 bg-ink/40" />
                    {c}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Footnote */}
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-ink/15 pt-12">
          <p className="font-display text-2xl md:text-3xl tracking-tight leading-[1.2] text-ink/85 max-w-xl">
            Because context matters — and the right team for one story isn&rsquo;t the right team for the next.
          </p>
          <p className="text-ink/65 text-base md:text-lg leading-relaxed md:pl-8">
            For each project we assemble a specialist crew — directors, DPs, editors, motion artists, sound designers, line producers, experience designers — from a vetted network across India.
          </p>
        </div>
      </div>
    </section>
  );
}
